import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import {
  campaignNameForEvent,
  formatEventDate,
  slugify,
  EVENT_TYPES,
  RSVP_MODES,
  type ManagedEvent,
  type SquadEvent,
} from "@/lib/events";
import { isValidCoverImageUrl } from "@/lib/cover-image";
import { assertCrewOrAdmin, assertTenantAdmin, isTenantAdmin } from "@/lib/tenants.functions";
import { sendTemplateEmail } from "@/lib/email-templates/send-email";

/** Public read: cross-post links are deliberately absent. */
const EVENT_COLS =
  "id, campaign_id, title, subtitle, slug, description, location, starts_at, ends_at, cover_image_url, status, featured, capacity, spectator_capacity, host_kind, price_cents, kit_price_cents, kit_label, payment_link_url, payment_link_with_kit_url, kit_only_payment_link_url, discount_payment_link_url, grants_pass_kind, grants_pass_discount_cents, accepted_pass_kinds, photo_consent_required, is_intro_session, event_type, requires_freq_allocation, rsvp_mode, partiful_url, facebook_event_url, meetup_event_url, eventbrite_url, posh_url, multigp_event_url, loops_last_call_url, created_at, updated_at";

/** Crew/admin read: same columns plus the test marker and series wiring. */
const ADMIN_EVENT_COLS = `${EVENT_COLS}, is_test, series_id, series_occurrence_date, detached_from_series, sunset_offset_minutes`;

/** Public, anon-key read: only rows the public SELECT policy exposes. */
export const listPublicEvents = createServerFn({ method: "GET" }).handler(async () => {
  const url = process.env["SUPABASE_URL"];
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"] ?? process.env["SUPABASE_ANON_KEY"];
  if (!url || !key) return { events: [] as SquadEvent[] };

  const supabasePublic = createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });

  const { data, error } = await supabasePublic
    .from("events")
    .select(EVENT_COLS)
    .eq("is_test", false)
    .in("status", ["planned", "ended"])
    .order("starts_at", { ascending: false });

  if (error) {
    console.error("public event read failed", error);
    return { events: [] as SquadEvent[] };
  }
  return { events: (data ?? []) as unknown as SquadEvent[] };
});

export type PublicEventMeta = {
  /** Pilot seats already claimed (confirmed + attended). */
  going: number;
  /** Pilots on the waitlist for this night. */
  waitlisted: number;
};

/**
 * Public read used by the landing page and `/events`: the same rows as
 * `listPublicEvents` plus the seat counts and the running pilot number, so
 * scarcity and social proof come from real data instead of adjectives.
 * Only aggregates leave the server — never a name or an email.
 */
export const listPublicEventsWithMeta = createServerFn({ method: "GET" }).handler(async () => {
  const { events } = await listPublicEvents();
  const empty = {
    events,
    meta: {} as Record<string, PublicEventMeta>,
    pilotCount: null as number | null,
  };

  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const upcomingIds = events
      .filter((e) => new Date(e.ends_at ?? e.starts_at).getTime() >= Date.now())
      .map((e) => e.id);

    const [counts, pilots] = await Promise.all([
      rsvpCountsFor(supabaseAdmin, upcomingIds),
      supabaseAdmin
        .from("pilots")
        .select("pilot_no")
        .not("pilot_no", "is", null)
        .order("pilot_no", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    const meta: Record<string, PublicEventMeta> = {};
    for (const [id, row] of counts) {
      meta[id] = { going: row.going, waitlisted: row.waitlisted };
    }
    const pilotCount = (pilots.data as { pilot_no: number | null } | null)?.pilot_no ?? null;
    return { events, meta, pilotCount };
  } catch (err) {
    console.error("public event meta read failed", err);
    return empty;
  }
});

/** Roll up pilot + community RSVP counts for a set of events. */
async function rsvpCountsFor(supabase: any, eventIds: string[]) {
  const counts = new Map<string, { going: number; waitlisted: number; spectators: number }>();
  if (eventIds.length === 0) return counts;
  const bump = (id: string, status: string, guests: number, role?: string | null) => {
    const row = counts.get(id) ?? { going: 0, waitlisted: 0, spectators: 0 };
    if (status === "going" || status === "attended") {
      if (role === "spectator") row.spectators += 1;
      else row.going += 1;
      row.spectators += guests || 0;
    } else if (status === "waitlisted") {
      row.waitlisted += 1;
    }
    counts.set(id, row);
  };

  const [pilots, community] = await Promise.all([
    supabase
      .from("event_rsvps")
      .select("event_id, status, guest_count, attend_role")
      .in("event_id", eventIds),
    supabase
      .from("community_rsvps")
      .select("event_id, status, guest_count")
      .in("event_id", eventIds),
  ]);

  for (const r of (pilots.data ?? []) as any[])
    bump(r.event_id, r.status, r.guest_count, r.attend_role);
  for (const r of (community.data ?? []) as any[]) bump(r.event_id, r.status, r.guest_count);
  return counts;
}

/** Crew and admin both read the full calendar; only admins can manage it. */
export const listAllEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertCrewOrAdmin(context);
    const canManage = await isTenantAdmin(context);

    const { data, error } = await context.supabase
      .from("events")
      .select(
        `${ADMIN_EVENT_COLS}, host_kind, host_pilot_id, pilots!left(first_name, last_name, callsign)`,
      )
      .order("starts_at", { ascending: false });
    if (error) {
      console.error("event read failed", error);
      return { events: [] as ManagedEvent[], canManage };
    }

    const rows = (data ?? []) as any[];
    const counts = await rsvpCountsFor(
      context.supabase,
      rows.map((r) => r.id as string),
    );

    const events = rows.map((row) => {
      const { pilots, ...rest } = row;
      return {
        ...rest,
        host_kind: (row.host_kind as "squadflight" | "community") ?? "squadflight",
        host_name: pilots
          ? (() => {
              const name = [pilots.first_name, pilots.last_name].filter(Boolean).join(" ");
              if (name && pilots.callsign) return `${name} (${pilots.callsign})`;
              return name || pilots.callsign || null;
            })()
          : null,
        counts: counts.get(row.id as string) ?? { going: 0, waitlisted: 0, spectators: 0 },
      };
    }) as ManagedEvent[];

    return { events, canManage };
  });

/** Read-only calendar for hosts: every SquadFlight event, drafts included. */
export const listPlanningEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: tenantId } = await context.supabase.rpc("current_tenant_id");
    const { data: isHost } = await context.supabase.rpc("is_host_pilot", {
      _user_id: context.userId,
      _tenant_id: tenantId as string,
    });
    if (!isHost && !(await isTenantAdmin(context))) throw new Error("Unauthorized");

    const { data, error } = await context.supabase
      .from("events")
      .select("id, title, slug, location, starts_at, ends_at, status, capacity, host_kind")
      .eq("host_kind", "squadflight")
      .order("starts_at", { ascending: true });

    if (error) {
      console.error("planning event read failed", error);
      return { events: [] as ManagedEvent[] };
    }
    return { events: (data ?? []) as unknown as ManagedEvent[] };
  });

const eventInput = z.object({
  title: z.string().trim().min(1).max(160),
  /** One-line subheadline under the title, e.g. "1-Hour First-Flight Experience". */
  subtitle: z.string().trim().max(160).nullable().optional(),
  slug: z.string().trim().max(80).optional(),
  description: z.string().trim().max(4000).nullable().optional(),
  location: z.string().trim().max(200).nullable().optional(),
  starts_at: z.string().trim().min(1),
  ends_at: z.string().trim().min(1).nullable().optional(),
  partiful_url: z.string().trim().url().max(500).nullable().optional(),
  facebook_event_url: z.string().trim().url().max(500).nullable().optional(),
  meetup_event_url: z.string().trim().url().max(500).nullable().optional(),
  eventbrite_url: z.string().trim().url().max(500).nullable().optional(),
  posh_url: z.string().trim().url().max(500).nullable().optional(),
  multigp_event_url: z.string().trim().url().max(500).nullable().optional(),
  payment_link_url: z.string().trim().url().max(1000).nullable().optional(),
  payment_link_with_kit_url: z.string().trim().url().max(1000).nullable().optional(),
  kit_only_payment_link_url: z.string().trim().url().max(1000).nullable().optional(),
  /** Reduced-price seat link, used when a discount code is applied. */
  discount_payment_link_url: z.string().trim().url().max(1000).nullable().optional(),
  /** Ladder wiring: does paying for this class hand out a discount code? */
  grants_pass_kind: z.string().trim().max(60).nullable().optional(),
  grants_pass_discount_cents: z.number().int().min(0).max(1_000_000).nullable().optional(),
  /** Discount-code kinds this class accepts. */
  accepted_pass_kinds: z.array(z.string().trim().max(60)).max(10).optional(),
  /** This night's held last-call email in Loops, linked from the low-seat alert. */
  loops_last_call_url: z.string().trim().url().max(1000).nullable().optional(),
  rsvp_mode: z.enum(RSVP_MODES).optional(),
  capacity: z.number().int().min(1).max(500).nullable().optional(),
  spectator_capacity: z.number().int().min(0).max(500).optional(),
  cover_image_url: z
    .string()
    .trim()
    .max(1000)
    .refine(isValidCoverImageUrl, "Enter an image link or upload a photo")
    .nullable()
    .optional(),
  status: z.enum(["draft", "planned", "ended", "cancelled"]).default("planned"),
  featured: z.boolean().default(false),
  price_cents: z.number().int().min(0).max(1_000_000).optional(),
  kit_price_cents: z.number().int().min(0).max(1_000_000).optional(),
  kit_label: z.string().trim().max(160).nullable().optional(),
  photo_consent_required: z.boolean().optional(),
  is_intro_session: z.boolean().optional(),
  event_type: z.enum(EVENT_TYPES).optional(),
  requires_freq_allocation: z.boolean().optional(),
});

/** Every event owns a campaign named `yyyy-mm-dd Event Name`. */
async function upsertEventCampaign(
  supabase: any,
  opts: { campaignId: string | null; title: string; startsAt: string; status: string },
): Promise<string | null> {
  const name = campaignNameForEvent(opts.startsAt, opts.title);
  const campaignStatus =
    opts.status === "ended" || opts.status === "cancelled" ? "ended" : "active";

  if (opts.campaignId) {
    const { error } = await supabase
      .from("campaigns")
      .update({
        name,
        type: "event",
        status: campaignStatus,
        start_date: opts.startsAt.slice(0, 10),
      })
      .eq("id", opts.campaignId);
    if (error) console.error("event campaign rename failed", error);
    return opts.campaignId;
  }

  // Re-running with the same name should reuse the campaign, not collide on it.
  const { data: known } = await supabase
    .from("campaigns")
    .select("id")
    .eq("name", name)
    .maybeSingle();
  if (known?.id) return known.id as string;

  const { data: row, error } = await supabase
    .from("campaigns")
    .insert({
      name,
      type: "event",
      status: campaignStatus,
      start_date: opts.startsAt.slice(0, 10),
    })
    .select("id")
    .single();

  if (error) {
    console.error("event campaign insert failed", error);
    return null;
  }
  return row.id as string;
}

export const createEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => eventInput.parse(data))
  .handler(async ({ context, data }) => {
    await assertTenantAdmin(context);

    const campaignId = await upsertEventCampaign(context.supabase, {
      campaignId: null,
      title: data.title,
      startsAt: data.starts_at,
      status: data.status,
    });

    const { data: row, error } = await context.supabase
      .from("events")
      .insert({
        title: data.title,
        subtitle: data.subtitle ?? null,
        slug: data.slug?.trim() || slugify(data.title),
        description: data.description ?? null,
        location: data.location ?? null,
        starts_at: data.starts_at,
        ends_at: data.ends_at ?? null,
        partiful_url: data.partiful_url ?? null,
        facebook_event_url: data.facebook_event_url ?? null,
        meetup_event_url: data.meetup_event_url ?? null,
        eventbrite_url: data.eventbrite_url ?? null,
        posh_url: data.posh_url ?? null,
        multigp_event_url: data.multigp_event_url ?? null,
        ...(data.rsvp_mode !== undefined ? { rsvp_mode: data.rsvp_mode } : {}),
        ...(data.capacity !== undefined ? { capacity: data.capacity } : {}),
        ...(data.spectator_capacity !== undefined
          ? { spectator_capacity: data.spectator_capacity }
          : {}),
        cover_image_url: data.cover_image_url ?? null,
        status: data.status,
        featured: data.featured,
        campaign_id: campaignId,
        ...(data.price_cents !== undefined ? { price_cents: data.price_cents } : {}),
        ...(data.kit_price_cents !== undefined ? { kit_price_cents: data.kit_price_cents } : {}),
        ...(data.kit_label !== undefined ? { kit_label: data.kit_label } : {}),
        ...(data.payment_link_url !== undefined ? { payment_link_url: data.payment_link_url } : {}),
        ...(data.payment_link_with_kit_url !== undefined
          ? { payment_link_with_kit_url: data.payment_link_with_kit_url }
          : {}),
        ...(data.kit_only_payment_link_url !== undefined
          ? { kit_only_payment_link_url: data.kit_only_payment_link_url }
          : {}),
        ...(data.discount_payment_link_url !== undefined
          ? { discount_payment_link_url: data.discount_payment_link_url }
          : {}),
        ...(data.grants_pass_kind !== undefined ? { grants_pass_kind: data.grants_pass_kind } : {}),
        ...(data.grants_pass_discount_cents !== undefined
          ? { grants_pass_discount_cents: data.grants_pass_discount_cents }
          : {}),
        ...(data.accepted_pass_kinds !== undefined
          ? { accepted_pass_kinds: data.accepted_pass_kinds }
          : {}),
        ...(data.loops_last_call_url !== undefined
          ? { loops_last_call_url: data.loops_last_call_url }
          : {}),
        ...(data.photo_consent_required !== undefined
          ? { photo_consent_required: data.photo_consent_required }
          : {}),
        ...(data.is_intro_session !== undefined ? { is_intro_session: data.is_intro_session } : {}),
        ...(data.event_type !== undefined ? { event_type: data.event_type } : {}),
        ...(data.requires_freq_allocation !== undefined
          ? { requires_freq_allocation: data.requires_freq_allocation }
          : {}),
      } as never)
      .select(ADMIN_EVENT_COLS)
      .single();

    if (error) {
      if (error.code === "23505") throw new Error("An event with that link name already exists.");
      console.error("event insert failed", error);
      throw new Error("Could not create that event.");
    }
    return { event: row as unknown as SquadEvent };
  });

export const updateEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    eventInput.partial().extend({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertTenantAdmin(context);
    const { id, ...rest } = data;

    const { data: current, error: readError } = await context.supabase
      .from("events")
      .select(ADMIN_EVENT_COLS)
      .eq("id", id)
      .single();
    if (readError || !current) throw new Error("That event no longer exists.");

    const updates: Record<string, any> = {};
    for (const [key, value] of Object.entries(rest)) {
      if (value !== undefined) updates[key] = value;
    }

    // Editing one night of a repeating series unlinks it, so series-wide
    // changes never overwrite the one-off edit.
    if ((current as any).series_id && Object.keys(updates).length > 0) {
      updates["detached_from_series"] = true;
    }

    const campaignId = await upsertEventCampaign(context.supabase, {
      campaignId: (current as any).campaign_id ?? null,
      title: (updates["title"] as string) ?? (current as any).title,
      startsAt: (updates["starts_at"] as string) ?? (current as any).starts_at,
      status: (updates["status"] as string) ?? (current as any).status,
    });
    updates["campaign_id"] = campaignId;

    const { data: row, error } = await context.supabase
      .from("events")
      .update(updates as never)
      .eq("id", id)
      .select(ADMIN_EVENT_COLS)
      .single();

    if (error) {
      console.error("event update failed", error);
      throw new Error("Could not update that event.");
    }
    return { event: row as unknown as SquadEvent };
  });

export const deleteEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ context, data }) => {
    await assertTenantAdmin(context);
    const { error } = await context.supabase.from("events").delete().eq("id", data.id);
    if (error) {
      console.error("event delete failed", error);
      throw new Error("Could not delete that event.");
    }
    return { ok: true };
  });

/** Tell everyone who RSVP'd that an event is off. Admin-only. */
export const notifyEventCancelled = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        eventId: z.string().uuid(),
        note: z.string().trim().max(500).optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertTenantAdmin(context);

    const { data: event, error } = await context.supabase
      .from("events")
      .select("id, title, starts_at, ends_at, location")
      .eq("id", data.eventId)
      .single();
    if (error || !event) throw new Error("That event no longer exists.");

    const ev = event as any;

    const [pilotRsvps, communityRsvps] = await Promise.all([
      context.supabase
        .from("event_rsvps")
        .select("guest_email, status, pilots!left(first_name, email)")
        .eq("event_id", data.eventId)
        .in("status", ["going", "waitlisted"]),
      context.supabase
        .from("community_rsvps")
        .select("status, community_contacts!left(first_name, full_name, email)")
        .eq("event_id", data.eventId)
        .in("status", ["going", "waitlisted"]),
    ]);

    const recipients = new Map<string, string | null>();
    for (const row of (pilotRsvps.data ?? []) as any[]) {
      const email = row.pilots?.email ?? row.guest_email;
      if (email) recipients.set(String(email).toLowerCase(), row.pilots?.first_name ?? null);
    }
    for (const row of (communityRsvps.data ?? []) as any[]) {
      const c = row.community_contacts;
      if (c?.email)
        recipients.set(
          String(c.email).toLowerCase(),
          c.first_name ?? (c.full_name ? String(c.full_name).split(" ")[0] : null),
        );
    }

    let sent = 0;
    for (const [email, firstName] of recipients) {
      try {
        const result = await sendTemplateEmail("event-cancelled", email, {
          templateData: {
            firstName,
            eventTitle: ev.title,
            eventWhen: formatEventDate(ev.starts_at, ev.ends_at),
            location: ev.location,
            note: data.note ?? null,
          },
          idempotencyKey: `event-cancelled-${data.eventId}-${email}`,
        });
        if (result.sent) sent += 1;
      } catch (e) {
        console.error("event cancelled email failed", e);
      }
    }

    return { sent, total: recipients.size };
  });

/** Admin-only: pilots flagged as hosts, for reassigning event ownership. */
export const listHostPilots = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertTenantAdmin(context);
    const { data, error } = await context.supabase
      .from("pilots")
      .select("id, first_name, last_name, callsign")
      .eq("is_host", true)
      .eq("status", "active")
      .order("first_name", { ascending: true });

    if (error) {
      console.error("listHostPilots failed", error);
      throw new Error("Could not load hosts.");
    }

    return {
      hosts: (data ?? []).map((p: any) => {
        const name = [p.first_name, p.last_name].filter(Boolean).join(" ");
        return {
          id: p.id as string,
          label: name && p.callsign ? `${name} (${p.callsign})` : name || p.callsign || "Pilot",
        };
      }),
    };
  });

/**
 * Admin-only correction: the host is always the owner, so this exists purely to
 * fix a mis-attributed community event.
 */
export const reassignEventHost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ eventId: z.string().uuid(), hostPilotId: z.string().uuid() }).parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertTenantAdmin(context);

    const { data: host, error: hostError } = await context.supabase
      .from("pilots")
      .select("id, is_host")
      .eq("id", data.hostPilotId)
      .maybeSingle();

    if (hostError || !host) throw new Error("That pilot could not be found.");
    if (!host.is_host) throw new Error("That pilot is not a host yet.");

    const { error } = await context.supabase
      .from("events")
      .update({ host_pilot_id: data.hostPilotId, host_kind: "community" } as never)
      .eq("id", data.eventId);

    if (error) {
      console.error("reassignEventHost failed", error);
      throw new Error("Could not reassign that event.");
    }
    return { ok: true };
  });
